import { useState, useEffect, useCallback } from "react";
import "./AboutPage.css";
import Footer from "../Component/NewFooter";
import SplashScreen from "../Component/SplashScreen";
import axios from "axios";

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
const CACHE_KEY      = "jeyshawn_about_data";

/* ── Helpers ───────────────────────────────────────────────────── */
function parseAboutData(db) {
  return {
    aboutUs: {
      heading:    db?.aboutUs?.heading    || "",
      paragraphs: Array.isArray(db?.aboutUs?.paragraphs) ? db.aboutUs.paragraphs : [],
      image:      db?.aboutUs?.image      || "",
    },
    mission: {
      title: db?.mission?.title || "",
      text:  db?.mission?.text  || "",
    },
    vision: {
      title: db?.vision?.title || "",
      text:  db?.vision?.text  || "",
    },
    bannerImage: db?.bannerImage || "",
  };
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

function AboutUs({ data }) {
  return (
    <section className="about-us">
      <div className="about-us__header">
        <h2 className="section-title">{data.heading}</h2>
        <span className="section-divider" />
      </div>

      <div className="about-us__body">
        <div className="about-us__img-wrap">
          <div className="about-us__img-accent" />
          <img
            src={data.image}
            alt="Offshore oil platform with workers"
            className="about-us__img"
          />
        </div>

        <div className="about-us__text">
          <div className="about_us_trans">
            {data.paragraphs.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function IMSPolicy({ mission, vision }) {
  return (
    <section className="ims-policy">
      <div className="ims-policy__inner">
        <h2 className="ims-policy__title">{mission.title}</h2>
        <p className="ims-policy__text">{mission.text}</p>
      </div>
      <div className="ims-policy__inner">
        <h2 className="ims-policy__title">{vision.title}</h2>
        <p className="ims-policy__text">{vision.text}</p>
      </div>
    </section>
  );
}

/* ── Page ──────────────────────────────────────────────────────── */
export default function AboutPage() {
  const cached = readCache();

  const [status, setStatus]     = useState(cached ? "ready" : "splash-loading");
  const [pageData, setPageData] = useState(cached || null);

  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/about`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseAboutData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch (err) {
      clearTimeout(timeoutId);
      if (background) {
        console.warn("Background refresh failed for /api/about:", err.message);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    doFetch(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  }
  if (status === "splash-loading") return <SplashScreen blocking />;

  const data = pageData;

  return (
    <div className="about_bg_color">
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      <div className="img_carrier">
        <img src={data.bannerImage} alt="" />
      </div>
      <div className="about-page">
        <AboutUs data={data.aboutUs} />
        <IMSPolicy mission={data.mission} vision={data.vision} />
      </div>
      <Footer />
    </div>
  );
}